import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { OC, FONT, CATEGORY, urgency } from '../constants/index.js';
import { useTheme } from '../constants/index.js';
import { useStore } from '../store/StoreProvider.js';

// --- Logo ---
function Logo({ size = 220, variant = 'fullLight', withWordmark = true, withTagline = true }) {
    const isDark = variant === 'fullDark' || variant === 'monoDark';
    const monoOrange = variant === 'orange';
    const monoOnly = variant === 'monoLight' || variant === 'monoDark' || variant === 'orange';
    const oFill = monoOrange ? OC.orange : (variant === 'monoDark' ? '#fff' : (variant === 'monoLight' ? OC.charcoal : OC.orange));
    const cFill = monoOrange ? OC.orange : (isDark ? '#FFFFFF' : OC.charcoal);
    const wordmarkInk = monoOrange ? OC.orange : (isDark ? '#FFFFFF' : OC.charcoal);
    const wordmarkAccent = monoOnly ? wordmarkInk : OC.orange;
    const markH = size * (withWordmark ? 0.46 : 1);
    const markW = markH * (120 / 80);
    const gradId = `oc-grad-${variant}-${size}`;

    return React.createElement('div', {
        style: { display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: withWordmark ? size * 0.04 : 0, fontFamily: FONT.display, lineHeight: 1 }
    },
        React.createElement('svg', {
            width: markW, height: markH, viewBox: '0 0 120 80', style: { display: 'block' }, 'aria-label': 'Opportunity Circle'
        },
            React.createElement('defs', null,
                React.createElement('linearGradient', { id: gradId, x1: '0', y1: '0', x2: '1', y2: '1' },
                    React.createElement('stop', { offset: '0%', stopColor: monoOnly ? oFill : '#FFB463' }),
                    React.createElement('stop', { offset: '55%', stopColor: monoOnly ? oFill : '#F97316' }),
                    React.createElement('stop', { offset: '100%', stopColor: monoOnly ? oFill : '#D9580B' })
                )
            ),
            React.createElement('path', {
                d: 'M 90 18 A 22 22 0 1 0 90 62',
                fill: 'none', stroke: cFill, strokeWidth: '12', strokeLinecap: 'round'
            }),
            React.createElement('circle', {
                cx: '48', cy: '40', r: '22',
                fill: 'none', stroke: `url(#${gradId})`, strokeWidth: '12'
            })
        ),
        withWordmark ? React.createElement('div', { style: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: size * 0.018 } },
            React.createElement('div', { style: { display: 'flex', alignItems: 'baseline', gap: size * 0.03, fontWeight: 800, letterSpacing: '-0.01em', fontSize: size * 0.115 } },
                React.createElement('span', { style: { color: wordmarkInk } }, 'OPPORTUNITY'),
                React.createElement('span', { style: { color: wordmarkAccent } }, 'CIRCLE')
            ),
            withTagline ? React.createElement('div', { style: { color: wordmarkInk, opacity: 0.7, fontFamily: FONT.body, fontWeight: 500, fontSize: size * 0.044, letterSpacing: '0.32em', textTransform: 'uppercase' } }, '— Sharing is Caring —') : null
        ) : null
    );
}

// --- LogoNav ---
function LogoNav({ theme = 'light', height = 28 }) {
    const isDark = theme === 'dark';
    const ink = isDark ? '#FFFFFF' : OC.charcoal;
    const orange = OC.orange;
    const markW = height * 1.5;
    const gradId = `oc-nav-grad-${theme}-${height}`;

    return React.createElement('div', {
        style: { display: 'inline-flex', alignItems: 'center', gap: 10, fontFamily: FONT.display, lineHeight: 1 }
    },
        React.createElement('svg', {
            width: markW, height: height, viewBox: '0 0 120 80', style: { display: 'block', flex: '0 0 auto' }
        },
            React.createElement('defs', null,
                React.createElement('linearGradient', { id: gradId, x1: '0', y1: '0', x2: '1', y2: '1' },
                    React.createElement('stop', { offset: '0%', stopColor: '#FFB463' }),
                    React.createElement('stop', { offset: '55%', stopColor: '#F97316' }),
                    React.createElement('stop', { offset: '100%', stopColor: '#D9580B' })
                )
            ),
            React.createElement('path', {
                d: 'M 90 18 A 22 22 0 1 0 90 62',
                fill: 'none', stroke: ink, strokeWidth: '12', strokeLinecap: 'round'
            }),
            React.createElement('circle', {
                cx: '48', cy: '40', r: '22',
                fill: 'none', stroke: `url(#${gradId})`, strokeWidth: '12'
            })
        ),
        React.createElement('span', { style: { fontWeight: 800, fontSize: height * 0.7, letterSpacing: '-0.01em', display: 'flex', gap: 4 } },
            React.createElement('span', { style: { color: ink } }, 'Opportunity'),
            React.createElement('span', { style: { color: orange } }, 'Circle')
        )
    );
}

// --- Button ---
function Button({ kind = 'primary', children, icon, iconRight, theme = 'light', size = 'md', fullWidth = false, onClick, style = {} }) {
    const t = useTheme(theme);
    const heights = { sm: 32, md: 40, lg: 48 };
    const fontSizes = { sm: 13, md: 15, lg: 16 };
    const padX = { sm: 12, md: 18, lg: 24 };
    const h = heights[size];

    const styles = {
        primary: { background: OC.orange, color: '#fff', border: 'none', boxShadow: t.shadow.sm },
        secondary: { background: theme === 'dark' ? t.bg.subtle : '#fff', color: t.text.primary, border: `1px solid ${t.border.base}` },
        ghost: { background: 'transparent', color: t.text.primary, border: 'none' },
        destructive: { background: OC.urgent, color: '#fff', border: 'none' },
        accentSubtle: {
            background: theme === 'dark' ? 'rgba(249,115,22,.14)' : OC.orangeMuted,
            color: OC.orange,
            border: theme === 'dark' ? '1px solid rgba(249,115,22,.22)' : `1px solid ${OC.orangeSubtle}`,
        },
    };

    return React.createElement('button', {
        onClick, style: { ...styles[kind], height: h, padding: `0 ${padX[size]}px`, fontFamily: FONT.body, fontWeight: 600, fontSize: fontSizes[size], borderRadius: 10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer', width: fullWidth ? '100%' : undefined, letterSpacing: '-0.005em', ...style }
    }, icon, children, iconRight);
}

// --- Badge ---
function Badge({ children, color, soft = true, theme = 'light', icon, style = {} }) {
    const t = useTheme(theme);
    const c = color || OC.orange;
    return React.createElement('span', {
        style: {
            display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 9px', borderRadius: 999,
            background: soft ? (theme === 'dark' ? `${c}26` : `${c}1F`) : c,
            color: soft ? c : '#fff', fontFamily: FONT.body, fontWeight: 600, fontSize: 11.5,
            lineHeight: 1, letterSpacing: '0.01em', whiteSpace: 'nowrap', ...style
        }
    }, icon ? React.createElement('span', { style: { display: 'inline-flex' } }, icon) : null, children);
}

// --- Icon ---
function Icon({ name, size = 18, color = 'currentColor', strokeWidth = 1.7 }) {
    const common = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: color, strokeWidth, strokeLinecap: 'round', strokeLinejoin: 'round' };

    const paths = {
        briefcase: React.createElement('g', null,
            React.createElement('rect', { x: '3', y: '7', width: '18', height: '13', rx: '2' }),
            React.createElement('path', { d: 'M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2' }),
            React.createElement('path', { d: 'M3 13h18' })
        ),
        cap: React.createElement('g', null,
            React.createElement('path', { d: 'M22 10 12 5 2 10l10 5 10-5Z' }),
            React.createElement('path', { d: 'M6 12v5c3 2 9 2 12 0v-5' })
        ),
        award: React.createElement('g', null,
            React.createElement('circle', { cx: '12', cy: '9', r: '6' }),
            React.createElement('path', { d: 'm8.21 13.89-1.7 7.11 5.49-3.4 5.49 3.4-1.7-7.11' })
        ),
        building: React.createElement('g', null,
            React.createElement('rect', { x: '4', y: '3', width: '16', height: '18', rx: '1.5' }),
            React.createElement('path', { d: 'M9 8h.01M15 8h.01M9 12h.01M15 12h.01M9 16h.01M15 16h.01' })
        ),
        spark: React.createElement('g', null,
            React.createElement('rect', { x: '3', y: '5', width: '18', height: '16', rx: '2' }),
            React.createElement('path', { d: 'M8 3v4M16 3v4M3 11h18' }),
            React.createElement('path', { d: 'M12 14l1 2 2 .3-1.5 1.4.4 2-1.9-1-1.9 1 .4-2L9 16.3 11 16Z' })
        ),
        search: React.createElement('g', null,
            React.createElement('circle', { cx: '11', cy: '11', r: '7' }),
            React.createElement('path', { d: 'm20 20-3.5-3.5' })
        ),
        bookmark: React.createElement('path', { d: 'M5 4a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v17l-7-4-7 4Z' }),
        bookmarkFilled: React.createElement('path', { d: 'M5 4a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v17l-7-4-7 4Z', fill: color, stroke: 'none' }),
        share: React.createElement('g', null,
            React.createElement('circle', { cx: '18', cy: '5', r: '3' }),
            React.createElement('circle', { cx: '6', cy: '12', r: '3' }),
            React.createElement('circle', { cx: '18', cy: '19', r: '3' }),
            React.createElement('path', { d: 'm8.6 13.5 6.8 4M15.4 6.5l-6.8 4' })
        ),
        arrowRight: React.createElement('g', null,
            React.createElement('path', { d: 'M5 12h14M13 5l7 7-7 7' })
        ),
        arrowUpRight: React.createElement('g', null,
            React.createElement('path', { d: 'M7 17 17 7M8 7h9v9' })
        ),
        check: React.createElement('path', { d: 'm4 12 5 5L20 6' }),
        x: React.createElement('g', null,
            React.createElement('path', { d: 'M6 6l12 12M18 6 6 18' })
        ),
        sun: React.createElement('g', null,
            React.createElement('circle', { cx: '12', cy: '12', r: '4' }),
            React.createElement('path', { d: 'M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41' })
        ),
        moon: React.createElement('path', { d: 'M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z' }),
        bell: React.createElement('g', null,
            React.createElement('path', { d: 'M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9' }),
            React.createElement('path', { d: 'M13.7 21a2 2 0 0 1-3.4 0' })
        ),
        mapPin: React.createElement('g', null,
            React.createElement('path', { d: 'M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z' }),
            React.createElement('circle', { cx: '12', cy: '10', r: '3' })
        ),
        clock: React.createElement('g', null,
            React.createElement('circle', { cx: '12', cy: '12', r: '9' }),
            React.createElement('path', { d: 'M12 7v5l3 2' })
        ),
        filter: React.createElement('g', null,
            React.createElement('path', { d: 'M3 5h18M6 12h12M10 19h4' })
        ),
        globe: React.createElement('g', null,
            React.createElement('circle', { cx: '12', cy: '12', r: '9' }),
            React.createElement('path', { d: 'M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18' })
        ),
        sparkle: React.createElement('g', null,
            React.createElement('path', { d: 'M12 3v6M12 15v6M3 12h6M15 12h6M5.6 5.6l4.2 4.2M14.2 14.2l4.2 4.2M5.6 18.4l4.2-4.2M14.2 9.8l4.2-4.2' })
        ),
        plus: React.createElement('g', null,
            React.createElement('path', { d: 'M12 5v14M5 12h14' })
        ),
        menu: React.createElement('g', null,
            React.createElement('path', { d: 'M4 6h16M4 12h16M4 18h16' })
        ),
        chevDown: React.createElement('path', { d: 'm6 9 6 6 6-6' }),
        chevRight: React.createElement('path', { d: 'm9 6 6 6-6 6' }),
        heart: React.createElement('path', { d: 'M20.8 5.6a5.5 5.5 0 0 0-7.8 0L12 6.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l8.8 8.8 8.8-8.8a5.5 5.5 0 0 0 0-7.8Z' }),
        send: React.createElement('path', { d: 'm22 2-7 20-4-9-9-4 20-7Z' }),
        mail: React.createElement('g', null,
            React.createElement('rect', { x: '3', y: '5', width: '18', height: '14', rx: '2' }),
            React.createElement('path', { d: 'm3 7 9 6 9-6' })
        ),
        home: React.createElement('path', { d: 'M3 11 12 3l9 8v10a1 1 0 0 1-1 1h-5v-7h-6v7H4a1 1 0 0 1-1-1Z' }),
        grid: React.createElement('g', null,
            React.createElement('rect', { x: '3', y: '3', width: '7', height: '7', rx: '1' }),
            React.createElement('rect', { x: '14', y: '3', width: '7', height: '7', rx: '1' }),
            React.createElement('rect', { x: '3', y: '14', width: '7', height: '7', rx: '1' }),
            React.createElement('rect', { x: '14', y: '14', width: '7', height: '7', rx: '1' })
        ),
        list: React.createElement('g', null,
            React.createElement('path', { d: 'M8 6h13M8 12h13M8 18h13' }),
            React.createElement('path', { d: 'M3.5 6h.01M3.5 12h.01M3.5 18h.01' })
        ),
        user: React.createElement('g', null,
            React.createElement('circle', { cx: '12', cy: '8', r: '4' }),
            React.createElement('path', { d: 'M4 21a8 8 0 0 1 16 0' })
        ),
        lock: React.createElement('g', null,
            React.createElement('rect', { x: '4', y: '11', width: '16', height: '10', rx: '2' }),
            React.createElement('path', { d: 'M8 11V7a4 4 0 0 1 8 0v4' })
        ),
        eye: React.createElement('g', null,
            React.createElement('path', { d: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z' }),
            React.createElement('circle', { cx: '12', cy: '12', r: '3' })
        ),
        eyeOff: React.createElement('g', null,
            React.createElement('path', { d: 'M10.6 6.2A9.7 9.7 0 0 1 12 5c6.5 0 10 7 10 7a18 18 0 0 1-3 3.7M6.6 6.6A18 18 0 0 0 2 12s3.5 7 10 7a9.7 9.7 0 0 0 4.4-1M9.9 9.9a3 3 0 0 0 4.2 4.2' }),
            React.createElement('path', { d: 'm3 3 18 18' })
        ),
        phone: React.createElement('path', { d: 'M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L20 13l2 4v3a1 1 0 0 1-1 1A17 17 0 0 1 4 5a1 1 0 0 1 1-1Z' }),
        arrowLeft: React.createElement('g', null,
            React.createElement('path', { d: 'M19 12H5M11 5l-7 7 7 7' })
        ),
        atSign: React.createElement('g', null,
            React.createElement('circle', { cx: '12', cy: '12', r: '4' }),
            React.createElement('path', { d: 'M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-4 8' })
        ),
        shieldCheck: React.createElement('g', null,
            React.createElement('path', { d: 'M12 3 4 6v6c0 5 3.5 7.5 8 9 4.5-1.5 8-4 8-9V6Z' }),
            React.createElement('path', { d: 'm9 12 2 2 4-4' })
        ),
        chart: React.createElement('g', null,
            React.createElement('path', { d: 'M3 3v18h18' }),
            React.createElement('rect', { x: '7', y: '12', width: '3', height: '6', rx: '0.5' }),
            React.createElement('rect', { x: '12', y: '8', width: '3', height: '10', rx: '0.5' }),
            React.createElement('rect', { x: '17', y: '4', width: '3', height: '14', rx: '0.5' })
        ),
        flag: React.createElement('g', null,
            React.createElement('path', { d: 'M4 22V4M4 4h12l-2 4 2 4H4' })
        ),
        shield: React.createElement('path', { d: 'M12 3 4 6v6c0 5 3.5 7.5 8 9 4.5-1.5 8-4 8-9V6Z' }),
    };

    const content = paths[name] || React.createElement('circle', { cx: '12', cy: '12', r: '9' });
    return React.createElement('svg', common, content);
}

// --- Avatar ---
function Avatar({ name = '', tone, size = 36, theme = 'light' }) {
    const initials = name.split(' ').filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase() || 'OC';
    const tints = ['#3B82F6', '#8B5CF6', '#22C55E', '#14B8A6', '#F97316', '#EAB308', '#EF4444', '#0EA5E9'];
    const h = name.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
    const bg = tone || tints[h % tints.length];
    return React.createElement('div', {
        style: {
            width: size, height: size, borderRadius: size * 0.28,
            background: `${bg}22`, color: bg,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: FONT.display, fontWeight: 700, fontSize: size * 0.36,
            flex: '0 0 auto', letterSpacing: '-0.02em',
        }
    }, initials);
}

// --- CategoryIcon ---
function CategoryIcon({ category = 'jobs', size = 38, theme = 'light' }) {
    const cat = CATEGORY[category] || CATEGORY.jobs;
    return React.createElement('div', {
        style: {
            width: size, height: size, borderRadius: 12,
            background: theme === 'dark' ? `${cat.color}26` : `${cat.color}14`,
            color: cat.color,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            flex: '0 0 auto',
        }
    }, React.createElement(Icon, { name: cat.icon, size: size * 0.5 }));
}

// --- Card ---          
function Card({ children, theme = 'light', padding = 20, hover = false, style = {} }) {
    const t = useTheme(theme);
    return React.createElement('div', {
        style: {
            background: t.bg.card,
            border: `1px solid ${t.border.base}`,
            borderRadius: 14,
            padding,
            ...style
        }
    }, children);
}

// --- MonoTag ---
function MonoTag({ children, theme = 'light', style = {} }) {
    const t = useTheme(theme);
    return React.createElement('span', {
        style: {
            fontFamily: FONT.mono, fontWeight: 500,
            fontSize: 11, color: t.text.secondary,
            padding: '3px 7px', borderRadius: 5,
            background: t.bg.subtle,
            border: `1px solid ${t.border.base}`,
            whiteSpace: 'nowrap',
            ...style
        }
    }, children);
}

// --- Deadline ---
function Deadline({ days, theme = 'light', size = 'sm' }) {
    const t = useTheme(theme);
    const u = urgency(days);
    const isExpired = u.tier === 'expired';
    const color = u.color === 'secondary' ? t.text.secondary : u.color;
    return React.createElement('span', {
        style: {
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontFamily: FONT.body, fontWeight: 600,
            fontSize: size === 'sm' ? 12 : 13, color,
            textDecoration: isExpired ? 'line-through' : 'none',
            whiteSpace: 'nowrap',
        }
    }, u.icon ? React.createElement('span', { 'aria-hidden': true, style: { fontSize: 10 } }, u.icon) : null, u.label);
}

// --- MetaChip ---
function MetaChip({ icon, children, theme }) {
    const t = useTheme(theme);
    return React.createElement('span', {
        style: {
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '4px 8px', borderRadius: 7,
            background: t.bg.subtle, color: t.text.secondary,
            fontSize: 12, fontWeight: 500,
        }
    }, React.createElement(Icon, { name: icon, size: 12 }), children);
}

// --- Toggle ---
function Toggle({ on, onChange, theme = 'light' }) {
    const t = useTheme(theme);
    return React.createElement('button', {
        onClick: onChange, role: 'switch', 'aria-checked': on,
        style: {
            width: 46, height: 27, borderRadius: 999, padding: 3,
            border: 'none', cursor: 'pointer',
            background: on ? OC.orange : (theme === 'dark' ? t.bg.subtle : '#D1D5DB'),
            display: 'inline-flex', alignItems: 'center',
            justifyContent: on ? 'flex-end' : 'flex-start',
            transition: 'background .18s ease', flex: '0 0 auto',
        }
    }, React.createElement('span', {
        style: {
            width: 21, height: 21, borderRadius: '50%',
            background: '#fff',
            boxShadow: '0 1px 3px rgba(0,0,0,.25)',
            transition: 'all .18s ease',
        }
    }));
}

// --- Input ---
function Input({ label, value, onChange, placeholder, type = 'text', theme = 'light', icon, hint, rightSlot, as = 'input', rows = 4 }) {
    const t = useTheme(theme);
    const [focus, setFocus] = useState(false);
    const Tag = as;
    return React.createElement('label', { style: { display: 'block', fontFamily: FONT.body } },
        label ? React.createElement('div', { style: { fontSize: 13, fontWeight: 600, color: t.text.primary, marginBottom: 7 } }, label) : null,
        React.createElement('div', {
            style: {
                display: 'flex', alignItems: as === 'textarea' ? 'flex-start' : 'center',
                gap: 9, background: t.bg.page,
                border: `1.5px solid ${focus ? OC.orange : t.border.base}`,
                borderRadius: 10, padding: as === 'textarea' ? '12px 13px' : '0 13px',
                height: as === 'textarea' ? 'auto' : 44,
                transition: 'border-color .15s ease',
            }
        },
            icon ? React.createElement('span', { style: { color: focus ? OC.orange : t.text.secondary, flex: '0 0 auto', marginTop: as === 'textarea' ? 2 : 0 } },
                React.createElement(Icon, { name: icon, size: 17 })
            ) : null,
            React.createElement(Tag, {
                type, value, rows: as === 'textarea' ? rows : undefined,
                onChange: (e) => onChange(e.target.value),
                onFocus: () => setFocus(true),
                onBlur: () => setFocus(false),
                placeholder,
                style: {
                    flex: 1, border: 'none', outline: 'none', background: 'transparent',
                    fontFamily: FONT.body, fontSize: 14.5, color: t.text.primary,
                    width: '100%', resize: as === 'textarea' ? 'vertical' : undefined,
                    lineHeight: as === 'textarea' ? 1.55 : 'normal', padding: 0,
                }
            }),
            rightSlot || null
        ),
        hint ? React.createElement('div', { style: { fontSize: 12, color: t.text.secondary, marginTop: 6 } }, hint) : null
    );
}

// --- Toast ---
function Toast() {
    const s = useStore();
    const t = useTheme(s.theme);
    if (!s.toast) return null;
    return React.createElement('div', {
        key: s.toast.key,
        style: {
            position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)',
            background: s.theme === 'dark' ? '#fff' : OC.charcoal,
            color: s.theme === 'dark' ? OC.charcoal : '#fff',
            padding: '12px 18px', borderRadius: 12, fontFamily: FONT.body,
            fontSize: 14, fontWeight: 600, display: 'inline-flex',
            alignItems: 'center', gap: 9, zIndex: 80,
            boxShadow: '0 12px 30px rgba(0,0,0,.28)',
            animation: 'ocToastIn .25s ease',
        }
    }, s.toast.icon ? React.createElement('span', { style: { color: OC.orange } },
        React.createElement(Icon, { name: s.toast.icon, size: 16 })
    ) : null, s.toast.message);
}


export {
  Logo, LogoNav, Button, Badge, Icon, Avatar, CategoryIcon, Card, MonoTag, Deadline, MetaChip, Toggle, Input, Toast,
};
